import type { Entity, Link, EntityType } from '@/types/entity';
import { computeDegree, computeMentionedBy } from './graph';

/**
 * 파생 coherence 층 (S1) — "현재 데이터에서 다음에 채워야 할 것"을 *파생*한다.
 * derive-don't-declare: 손으로 박은 경보가 아니라 entities/links의 순수 함수.
 * OverviewView의 흩어진 인라인 검출을 여기로 단일화한다(detectors 재사용).
 * 링크 + 산문 @멘션(incoming refs, S2 흡수 배관)을 evidence·고립 판정에 반영.
 * 레이아웃·앵커 의존 없음 — 읽기전용 파생.
 */

const str = (v: unknown) => String(v ?? '').trim();

export type GapKind =
  | 'open-question'
  | 'missing-conflict'
  | 'isolated'
  | 'unlinked-beat'
  | 'empty-core-field'
  | 'draft-canon';

export type GapTone = 'ember' | 'violet' | 'gold' | 'star';

export interface GapSignal {
  id: string; // 안정 키: `${kind}:${entityId}[:${field}]`
  kind: GapKind;
  entityId: string;
  entityType: EntityType;
  name: string;
  title: string; // 무엇을 할지(초대)
  reason: string; // 왜 gap인지
  tone: GapTone;
  score: number;
}

/** 관계를 가질 법한 타입(고립 판정 대상) */
const LINKABLE: EntityType[] = ['character', 'faction', 'planet', 'location'];

/** 타입별 '핵심' 필드 — 비면 서사의 알맹이가 빈 것. 얇게 유지(엔진 척추만). */
const CORE_FIELDS: Partial<Record<EntityType, { key: string; label: string }[]>> = {
  character: [
    { key: 'wound', label: '상처' },
    { key: 'lie', label: '거짓' },
    { key: 'need', label: '진짜필요' },
  ],
  planet: [
    { key: 'axis', label: '철학 축' },
    { key: 'role', label: '서사 역할' },
  ],
  concept: [{ key: 'definition', label: '정의' }],
};

// --- 개별 detector (단일 소스 — Overview 타일도 이걸 재사용) ---

export const detectOpenQuestions = (es: Entity[]): Entity[] =>
  es.filter((e) => e.type === 'question' && e.fields.status !== 'resolved');

export const detectMissingConflict = (es: Entity[]): Entity[] =>
  es.filter((e) => e.type === 'beat' && !str(e.fields.conflict));

export const detectDraft = (es: Entity[]): Entity[] => es.filter((e) => e.canonStatus !== 'canon');

export function detectIsolated(es: Entity[], links: Link[]): Entity[] {
  // 공유 인덱스(graph) 단일 소스: 명시 링크 차수·incoming 멘션 둘 다 0이면 고립.
  // (computeMentionedBy가 self-ref·stale id를 제외하므로 가짜 연결 없음.)
  const degree = computeDegree(links);
  const mentionedBy = computeMentionedBy(es);
  return es.filter((e) => LINKABLE.includes(e.type) && !degree.get(e.id) && !mentionedBy.get(e.id));
}

export const detectUnlinkedBeat = (es: Entity[]): Entity[] =>
  es.filter((e) => e.type === 'beat' && !str(e.fields.storylineId));

// --- 점수화: impact(kind base) + evidence(연결/등장) + recency(최근 손댐=의도) ---
// draft 단계 노이즈 방어를 위해 연결성(결정·고립·연결) > 커버리지(빈 필드·캐논) 가중.

const KIND_BASE: Record<GapKind, { base: number; tone: GapTone; verb: string }> = {
  'open-question': { base: 3.0, tone: 'ember', verb: '결정을 닫아라' },
  isolated: { base: 2.8, tone: 'violet', verb: '관계를 이어라' },
  'missing-conflict': { base: 2.4, tone: 'ember', verb: '갈등을 적어라' },
  'unlinked-beat': { base: 2.2, tone: 'ember', verb: '스토리라인에 배치' },
  'empty-core-field': { base: 2.0, tone: 'star', verb: '핵심을 채워라' },
  'draft-canon': { base: 1.2, tone: 'gold', verb: '캐논 확정' },
};

function recencyBoost(updatedAt: number, now: number): number {
  const days = (now - updatedAt) / 86_400_000;
  if (days < 1) return 0.8;
  if (days < 7) return 0.4;
  return 0;
}

/**
 * 모든 gap을 점수순으로. 호출부가 top-K를 취한다.
 * now는 테스트 결정성을 위해 주입 가능(기본 Date.now()).
 */
export function deriveCoherenceGaps(entities: Entity[], links: Link[], now: number = Date.now()): GapSignal[] {
  // 공유 파생 인덱스(graph)에서 연결성 신호를 읽음(단일 소스 — coherence·도감 공통).
  const idSet = new Set(entities.map((e) => e.id));
  const degree = computeDegree(links);
  const mentionedBy = computeMentionedBy(entities, idSet);
  // evidence = 링크 차수 + incoming 멘션. 연결·등장이 많을수록 그 엔티티의 gap이 더 중요.
  const connOf = (id: string) => (degree.get(id) ?? 0) + (mentionedBy.get(id) ?? 0);
  const evidenceOf = (e: Entity) => Math.min(connOf(e.id) * 0.3, 1.5);

  const signals: GapSignal[] = [];
  const add = (kind: GapKind, e: Entity, title: string, reason: string, fieldKey?: string) => {
    const k = KIND_BASE[kind];
    signals.push({
      id: fieldKey ? `${kind}:${e.id}:${fieldKey}` : `${kind}:${e.id}`,
      kind,
      entityId: e.id,
      entityType: e.type,
      name: e.name,
      title,
      reason,
      tone: k.tone,
      score: k.base + evidenceOf(e) + recencyBoost(e.updatedAt, now),
    });
  };

  for (const e of detectOpenQuestions(entities)) add('open-question', e, KIND_BASE['open-question'].verb, str(e.fields.desc) || '열린 질문');
  for (const e of detectIsolated(entities, links)) add('isolated', e, KIND_BASE.isolated.verb, '관계망에 닿지 않음');
  for (const e of detectMissingConflict(entities)) add('missing-conflict', e, KIND_BASE['missing-conflict'].verb, str(e.fields.summary) || '긴장 미기입 비트');
  for (const e of detectUnlinkedBeat(entities)) add('unlinked-beat', e, KIND_BASE['unlinked-beat'].verb, '스토리라인 미지정');
  for (const e of detectDraft(entities)) add('draft-canon', e, KIND_BASE['draft-canon'].verb, `상태: ${e.canonStatus}`);

  // 빈 핵심필드 — 증거(연결/등장) 있을 때만(초고 노이즈 폭발 방지)
  for (const e of entities) {
    const core = CORE_FIELDS[e.type];
    if (!core) continue;
    const hasEvidence = connOf(e.id) > 0;
    if (!hasEvidence) continue;
    for (const f of core) {
      if (!str(e.fields[f.key])) add('empty-core-field', e, `${f.label} 비어있음`, '핵심 필드 공란', f.key);
    }
  }

  // 점수 desc, 동점은 id asc로 결정적(삽입순 의존 제거)
  signals.sort((a, b) => b.score - a.score || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return signals;
}
